/* ==========================================================================
   td-upsell-popup.js
   Metaobject-driven upsell popup. Vanilla JS, no dependencies, defer-loaded.
   Reads the JSON island #td-upsell-popup-data emitted by
   snippets/td-upsell-popup.liquid, matches the current context, and shows a
   modal that is BUILT EAGERLY (hidden) at arm time.

   display_trigger is a LIST: an entry can carry several triggers and fires on
   whichever hits FIRST; once shown, all others disarm (fireOnce + disarmAll).
   Triggers: delay | exit_intent | add_to_cart | click | cart_value.

   ONLY the `click` trigger intercepts. It SUSPENDS the original action of a
   `trigger_selector` element (e.g. #offer-buy-now), shows the popup, and RESUMES
   the original action on resolve. It never replaces the action. The revenue path
   is protected by 8 failsafe layers (see below) — any uncertainty → the original
   click proceeds untouched (fail open).

   Interop rules (from the theme's existing upsell scripts):
     - z-index handled in CSS at 2147483000 (below upsell-blocker's toast).
     - NEVER set the `_upsell_for` line item property — td-upsell-cascade.js
       would dedupe/remove the line.
     - Cart refresh reuses window.upcartRefreshCart() + cart:refresh/cart:updated.
     - cart_value hooks the shared fetch-wrap + one /cart.js read on arm. NO new
       polling interval.
     - localStorage namespace: td_upsell_popup_*  DOM ids: #td-upsell-popup*

   Kill switches (checked first, no deploy needed):
     - URL ?td_upsell_off=1
     - localStorage.setItem('td_upsell_popup_disable','1')
   ========================================================================== */
(function () {
  "use strict";

  if (window.__tdUpsellPopup) return;
  window.__tdUpsellPopup = true;

  // ---- Layer 6: independent kill switches (before anything else) -----------
  try {
    if (/[?&]td_upsell_off=1(&|$)/.test(window.location.search)) return;
  } catch (e) {}
  try {
    if (localStorage.getItem("td_upsell_popup_disable") === "1") return;
  } catch (e) {}

  // ---- Layer 5: global error boundary. Any throw → disable for the session --
  try {
    main();
  } catch (fatal) {
    try {
      sessionStorage.setItem("td_upsell_popup_session_disable", "1");
    } catch (e) {}
    if (window.console) {
      try {
        console.warn("[td-upsell] fatal error — disabled for session", fatal);
      } catch (e) {}
    }
  }

  function main() {
    var Z_NAMESPACE = "td_upsell_popup_";
    var SESSION_SHOWN_KEY = "td_upsell_popup_shown_session";
    var SESSION_ID_KEY = "td_upsell_popup_sid";
    var SESSION_DISABLE_KEY = "td_upsell_popup_session_disable";

    var origFetch = window.fetch; // pre-wrap, for internal /cart.js reads

    function sessionDisabled() {
      try {
        return sessionStorage.getItem(SESSION_DISABLE_KEY) === "1";
      } catch (e) {
        return false;
      }
    }
    function sessionDisable() {
      try {
        sessionStorage.setItem(SESSION_DISABLE_KEY, "1");
      } catch (e) {}
    }
    if (sessionDisabled()) return; // error budget already spent this session

    function debug() {
      return !!window.TD_UPSELL_DEBUG || !!(payload && payload.debug);
    }
    function log() {
      if (debug() && window.console) console.log.apply(console, ["[td-upsell]"].concat([].slice.call(arguments)));
    }

    // ---- Parse the data island ---------------------------------------------
    var dataEl = document.getElementById("td-upsell-popup-data");
    if (!dataEl) return;
    var payload;
    try {
      payload = JSON.parse(dataEl.textContent);
    } catch (e) {
      log("bad JSON", e);
      return;
    }
    if (!payload || !Array.isArray(payload.entries) || !payload.entries.length) {
      log("no entries");
      return;
    }

    // ---- A/B test arms -------------------------------------------------------
    // An entry with ab_test_enabled carries ab_variations: [{key, name, percent,
    // products}]. Each visitor gets one arm per entry, picked by percent and kept
    // in localStorage so they see the same offer on every page and visit (an arm
    // that no longer exists is re-picked). The arm's products replace the entry's
    // own; entry.ab_variant / ab_variant_name go to tracking and the line props.
    // Percents need not sum to 100 (they are relative); all 0 = equal split.
    // No arms = the entry is untouched.
    function abKey(handle) {
      return "td_upsell_popup_ab_" + handle;
    }
    function pickArm(arms) {
      var total = 0;
      arms.forEach(function (a) {
        total += Math.max(0, parseFloat(a.percent) || 0);
      });
      if (total <= 0) return arms[Math.floor(Math.random() * arms.length)];
      var r = Math.random() * total;
      for (var i = 0; i < arms.length; i++) {
        r -= Math.max(0, parseFloat(arms[i].percent) || 0);
        if (r < 0) return arms[i];
      }
      return arms[arms.length - 1];
    }
    payload.entries.forEach(function (entry) {
      var arms = (entry.ab_variations || []).filter(function (a) {
        return a && a.key && Array.isArray(a.products) && a.products.length;
      });
      if (!arms.length) return;
      var arm = null;
      var stored = null;
      try {
        stored = localStorage.getItem(abKey(entry.handle));
      } catch (e) {}
      arms.forEach(function (a) {
        if (a.key === stored) arm = a;
      });
      if (!arm) {
        arm = pickArm(arms);
        try {
          localStorage.setItem(abKey(entry.handle), arm.key);
        } catch (e) {}
      }
      entry.products = arm.products;
      entry.ab_variant = arm.key;
      entry.ab_variant_name = arm.name || arm.key;
    });
    payload.entries = payload.entries.filter(function (e) {
      return e.products && e.products.length;
    });
    if (!payload.entries.length) {
      log("no entries with products");
      return;
    }

    var COUNTRY = payload.country || "";
    var PAGE_HANDLE = payload.page_handle || "";
    var PAGE_PATH = window.location.pathname;
    var ALLOWED_PT = ["product", "page", "collection", "index", "cart"];
    var PAGE_TYPE = ALLOWED_PT.indexOf(payload.page_type) !== -1 ? payload.page_type : "other";
    var ENDPOINT = window.TD_UPSELL_TRACKING_ENDPOINT || payload.tracking_endpoint || "";

    // ---- Matching -----------------------------------------------------------
    function matches(entry) {
      var type = entry.trigger_type || "all";
      if (type === "all") return true;
      if (type === "product") {
        return PAGE_TYPE === "product" && !!PAGE_HANDLE && (entry.trigger_products || []).indexOf(PAGE_HANDLE) !== -1;
      }
      if (type === "collection") {
        return PAGE_TYPE === "collection" && !!PAGE_HANDLE && (entry.trigger_collections || []).indexOf(PAGE_HANDLE) !== -1;
      }
      if (type === "page") {
        return PAGE_TYPE === "page" && !!PAGE_HANDLE && (entry.trigger_pages || []).indexOf(PAGE_HANDLE) !== -1;
      }
      return false;
    }

    // ---- Frequency cap / session suppression -------------------------------
    function sessionShownSet() {
      try {
        return JSON.parse(sessionStorage.getItem(SESSION_SHOWN_KEY) || "[]");
      } catch (e) {
        return [];
      }
    }
    function markSessionShown(handle) {
      try {
        var s = sessionShownSet();
        if (s.indexOf(handle) === -1) s.push(handle);
        sessionStorage.setItem(SESSION_SHOWN_KEY, JSON.stringify(s));
      } catch (e) {}
    }
    function capKey(handle) {
      return Z_NAMESPACE + "cap_" + handle;
    }
    // frequency_mode gates ALL storage. Valid: always | once_session | once_ever
    // | days. Unknown/empty → once_session.
    function freqMode(entry) {
      var m = String(entry.frequency_mode || "").trim().toLowerCase();
      if (m === "always" || m === "once_session" || m === "once_ever" || m === "days") return m;
      return "once_session";
    }
    function isCapped(entry) {
      if (debug()) return false;
      var mode = freqMode(entry);
      if (mode === "always") return false; // never read any key — fire every time
      if (mode === "once_session") {
        return sessionShownSet().indexOf(entry.handle) !== -1;
      }
      if (mode === "once_ever") {
        try {
          return !!localStorage.getItem(capKey(entry.handle)); // presence = shown before
        } catch (e) {
          return false;
        }
      }
      if (mode === "days") {
        var days = parseInt(entry.frequency_cap_days, 10) || 0;
        if (days <= 0) return false;
        try {
          var ts = parseInt(localStorage.getItem(capKey(entry.handle)), 10);
          if (!ts) return false;
          return Date.now() - ts < days * 86400000;
        } catch (e) {
          return false;
        }
      }
      return false;
    }
    // Mode checked BEFORE any write. `always` writes NOTHING (no session, no local).
    function recordShown(entry) {
      var mode = freqMode(entry);
      if (mode === "always") return;
      if (mode === "once_session") {
        markSessionShown(entry.handle);
        return;
      }
      if (mode === "once_ever" || mode === "days") {
        try {
          localStorage.setItem(capKey(entry.handle), String(Date.now()));
        } catch (e) {}
        return;
      }
    }

    // ---- Pick the winning entry --------------------------------------------
    var eligible = payload.entries.filter(matches).filter(function (e) {
      return !isCapped(e);
    });
    if (!eligible.length) {
      log("nothing eligible");
      return;
    }
    eligible.sort(function (a, b) {
      return (parseInt(b.priority, 10) || 0) - (parseInt(a.priority, 10) || 0);
    });
    var eligibleEntries = eligible; // ALL eligible on this page (per-bundle add_to_cart selection)
    var builtEntry = null; // which entry the overlay was last built for (rebuild when ENTRY changes)
    var ENTRY = eligible[0];
    log("winning entry", ENTRY.handle, ENTRY);

    // ---- Tracking (+ Layer 7 observability) --------------------------------
    var FIRED_TRIGGER = null;
    function sessionId() {
      try {
        var sid = sessionStorage.getItem(SESSION_ID_KEY);
        if (!sid) {
          sid = "s_" + Date.now().toString(36) + "_" + Math.random().toString(36).slice(2, 10);
          sessionStorage.setItem(SESSION_ID_KEY, sid);
        }
        return sid;
      } catch (e) {
        return "s_nostore";
      }
    }
    function track(event, extra) {
      try {
        if (!ENDPOINT) return;
        extra = extra || {};
        var body = {
          event: event,
          entry: ENTRY.handle,
          internal_name: ENTRY.internal_name || "",
          placement: "popup",
          market: COUNTRY,
          page_type: PAGE_TYPE,
          page_handle: PAGE_HANDLE,
          page_path: PAGE_PATH,
          fired_trigger: FIRED_TRIGGER,
          variant_id: extra.variant_id || null,
          value: extra.value || null,
          session_id: sessionId(),
          timestamp: Date.now()
        };
        if (extra.reason) body.reason = extra.reason;
        if (ENTRY.ab_variant) {
          body.ab_variant = ENTRY.ab_variant;
          body.ab_variant_name = ENTRY.ab_variant_name;
        }
        var json = JSON.stringify(body);
        // Transport notes:
        //  - text/plain (a CORS-safe content type) keeps this a "simple" request —
        //    no OPTIONS preflight. Apps Script has no doOptions, so application/json
        //    would trip CORS and the request would never leave the browser.
        //  - We use fetch (NOT sendBeacon) because Apps Script doPost answers with a
        //    302 to googleusercontent.com. sendBeacon does NOT follow redirects — it
        //    dies at the 302 and the payload never lands. fetch follows the redirect.
        //  - keepalive:true makes the request survive page unload the way a beacon
        //    would, so dismiss/accept still send if the page navigates away right
        //    after. Payload is tiny, well under the ~64KB keepalive body cap.
        // Body stays the exact same JSON string.
        if (typeof fetch === "function") {
          fetch(ENDPOINT, {
            method: "POST",
            headers: { "Content-Type": "text/plain" },
            mode: "no-cors",
            body: json,
            keepalive: true
          }).catch(function () {});
        } else if (navigator.sendBeacon) {
          // Fallback only if fetch is somehow unavailable (effectively never).
          navigator.sendBeacon(ENDPOINT, new Blob([json], { type: "text/plain" }));
        }
      } catch (e) {}
    }
    function failsafeEvent(reason) {
      track("failsafe", { reason: reason });
    }

    // ---- DOM helpers --------------------------------------------------------
    function el(tag, cls, attrs) {
      var n = document.createElement(tag);
      if (cls) n.className = cls;
      if (attrs) for (var k in attrs) if (attrs.hasOwnProperty(k)) n.setAttribute(k, attrs[k]);
      return n;
    }
    function svgClose() {
      return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>';
    }
    function svgArrow() {
      return '<svg class="td-upsell-popup__flavor-select__arrow" xmlns="http://www.w3.org/2000/svg" width="20" height="10" viewBox="0 0 20 10" fill="none"><path d="M0.5 0.5L10 9.12069L19.5 0.5" stroke="#775E4D" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    }
    function imageFor(variant, product) {
      // Per-slot image_override wins for the whole card; else the variant image
      // (swaps with the dropdown); else the product image.
      return (product && product.image_override) || variant.image || (product && product.image) || "";
    }
    function pct(compare, price) {
      if (!compare || compare <= price) return null;
      return Math.round(((compare - price) / compare) * 100);
    }

    // ---- Build (once, eagerly, hidden) — Layer 1 ---------------------------
    var overlay = null;
    var prevFocus = null;
    var popupUsable = false;
    var countdownEl = null; // timer <span>, if any
    var countdownTimer = null; // setInterval handle
    var countdownStarted = false;

    // ---- Free shipping line ------------------------------------------------
    // Shows how far the cart is from free shipping (or that it's reached),
    // with a progress bar, under the intro line (Figma 11217:137615). Threshold
    // comes from the theme setting (per market, in minor units). On a click
    // intercept the main item is NOT in the cart yet, so its price is looked up
    // and counted. Any doubt → stays hidden.
    var fsBox = null;
    var fsLine = null;
    var fsFill = null;

    function fsConfig() {
      var fs = payload.free_shipping;
      if (!fs || !fs.enabled || ENTRY.hide_free_shipping === true) return null;
      if (typeof fs.threshold !== "number" || !(fs.threshold > 0)) return null;
      return fs;
    }

    function routeRoot() {
      try {
        var r = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
        if (r) return r.charAt(r.length - 1) === "/" ? r : r + "/";
      } catch (e) {}
      return "/";
    }

    function fmtMoney(cents, currency) {
      var amount = cents / 100;
      var whole = amount % 1 === 0;
      try {
        return new Intl.NumberFormat(document.documentElement.lang || undefined, {
          style: "currency",
          currency: currency,
          minimumFractionDigits: whole ? 0 : 2,
          maximumFractionDigits: whole ? 0 : 2
        }).format(amount);
      } catch (e) {
        return amount.toFixed(whole ? 0 : 2) + " " + currency;
      }
    }

    // Price (minor units) of the item the intercepted button is about to add.
    // Resolves 0 when nothing is pending (passive trigger: the cart is already
    // complete), null when it can't be priced.
    function pendingMainPrice() {
      var isButton = false;
      try {
        isButton = !!(pendingTarget && (!pendingTarget.closest || !pendingTarget.closest("a[href]")));
      } catch (e) {}
      if (!isButton) return Promise.resolve(0);
      var vid = null;
      try {
        vid = resolveClickVariant(pendingTarget);
      } catch (e) {}
      if (!vid) return Promise.resolve(null);
      var qty = 1;
      try {
        var form = pendingTarget.closest('form[action*="/cart/add"]');
        var q = form && form.querySelector('[name="quantity"]');
        if (q && Number(q.value) > 0) qty = Number(q.value);
      } catch (e) {}
      return origFetch(routeRoot() + "variants/" + vid + ".js", { headers: { Accept: "application/json" } })
        .then(function (r) {
          return r.ok ? r.json() : null;
        })
        .then(function (v) {
          return v && typeof v.price === "number" ? v.price * qty : null;
        });
    }

    function updateFreeShipping() {
      var fs = fsConfig();
      if (!fs || !fsBox) return;
      // add_to_cart can fire while the theme's /cart/add is still in flight (the
      // instant solo-generic path in installCartWrap). Reading /cart.js then
      // returned the cart WITHOUT the item being added, and a passive fire prices
      // the pending item at 0, so the bar ignored it. Read after the add settles.
      var settled = addInFlight ? addInFlight.then(noop, noop) : Promise.resolve();
      var cartP = settled
        .then(function () {
          return origFetch(routeRoot() + "cart.js", { headers: { Accept: "application/json" } });
        })
        .then(function (r) {
          return r.json();
        });
      Promise.all([cartP, pendingMainPrice()])
        .then(function (res) {
          var cart = res[0];
          var mainPrice = res[1];
          if (!cart || typeof cart.total_price !== "number" || mainPrice === null) return;
          var gap = fs.threshold - (cart.total_price + mainPrice);
          // [amount] → the missing amount (bold, red); **text** → bold. Built as
          // nodes, never innerHTML. Reached → the reached text, full bar.
          var reached = gap <= 0;
          var tpl = String((reached ? fs.reached_text : fs.away_text) || "");
          if (!tpl) return;
          fsLine.textContent = "";
          tpl.split(/(\[amount\]|\*\*[^*]+\*\*)/).forEach(function (part) {
            if (!part) return;
            if (part === "[amount]") {
              var amt = document.createElement("strong");
              amt.className = "td-upsell-popup__fs-amount";
              amt.textContent = fmtMoney(Math.max(gap, 0), cart.currency);
              fsLine.appendChild(amt);
            } else if (/^\*\*[^*]+\*\*$/.test(part)) {
              var b = document.createElement("strong");
              b.textContent = part.slice(2, -2);
              fsLine.appendChild(b);
            } else {
              fsLine.appendChild(document.createTextNode(part));
            }
          });
          var pctDone = Math.max(0, Math.min(100, ((fs.threshold - gap) / fs.threshold) * 100));
          fsFill.style.width = pctDone + "%";
          fsBox.hidden = false;
          log("free shipping", { threshold: fs.threshold, cart: cart.total_price, main: mainPrice, gap: gap });
        })
        .catch(function (e) {
          log("free shipping read failed", e);
        });
    }

    function buildModal() {
      builtEntry = ENTRY; // remember which entry this overlay renders (rebuild when ENTRY changes)
      overlay = el("div", "td-upsell-popup td-upsell-popup__overlay", {
        id: "td-upsell-popup-overlay",
        hidden: "" // UA display:none — survives CSS failure; author .is-open overrides
      });
      // Per-entry colour overrides — set CSS custom properties on the overlay root
      // (they inherit to the bar, badge, CTA). Empty/absent → var stays unset →
      // the CSS fallback (default theme) applies. Enables per-market colour via a
      // dedicated entry (e.g. a blue DACH popup).
      if (ENTRY.bar_color) overlay.style.setProperty("--tdup-bar-bg", ENTRY.bar_color);
      if (ENTRY.badge_color) overlay.style.setProperty("--tdup-badge-bg", ENTRY.badge_color);
      if (ENTRY.cta_color) overlay.style.setProperty("--tdup-cta-bg", ENTRY.cta_color);

      var modal = el("div", "td-upsell-popup__modal", {
        role: "dialog",
        "aria-modal": "true",
        "aria-label": ENTRY.subheadline || ENTRY.headline || "Special offer",
        id: "td-upsell-popup-modal"
      });

      // Optional top bar (header text + optional VISUAL-ONLY countdown).
      var topbar = buildTopbar();
      if (topbar) {
        modal.appendChild(topbar);
        modal.classList.add("td-upsell-popup__modal--has-bar"); // squares the modal's top corners
      }

      // Close (X): inside the announcement bar (top-right of the brown bar) when
      // a bar is present, else fallback to the white body top-right. Behavior
      // (dismiss/tracking/resume) is identical either way.
      var close = el("button", "td-upsell-popup__close", { type: "button", "aria-label": "Close" });
      close.innerHTML = svgClose();
      close.addEventListener("click", function () {
        dismiss("close");
      });
      if (topbar) topbar.appendChild(close);
      else modal.appendChild(close);

      // Header shows ONLY the single intro line (subheadline). headline / body /
      // badge_text are intentionally not rendered in this position (Figma).
      var header = el("div", "td-upsell-popup__header");
      if (ENTRY.subheadline) {
        var sub = el("p", "td-upsell-popup__subtitle");
        sub.textContent = ENTRY.subheadline;
        header.appendChild(sub);
      }
      modal.appendChild(header);

      fsBox = el("div", "td-upsell-popup__fsbox");
      fsBox.hidden = true;
      fsLine = el("p", "td-upsell-popup__fs");
      fsBox.appendChild(fsLine);
      var fsTrack = el("div", "td-upsell-popup__fs-track");
      fsFill = el("div", "td-upsell-popup__fs-fill");
      fsTrack.appendChild(fsFill);
      fsBox.appendChild(fsTrack);
      header.appendChild(fsBox);

      var products = el("div", "td-upsell-popup__products");
      (ENTRY.products || []).forEach(function (product) {
        products.appendChild(buildCard(product));
      });
      // One product → narrow the modal (~272px, Figma) so the single card keeps its
      // size instead of stretching across the full 2-up width.
      if ((ENTRY.products || []).length === 1) {
        modal.classList.add("td-upsell-popup__modal--single");
      }
      modal.appendChild(products);

      // Skip block: intro note + underlined decline link (Figma), on ONE line.
      // Intro line = translatable metaobject field `skip_note` (falls back to the
      // English constant if the field is empty/absent).
      var skipWrap = el("div", "td-upsell-popup__skip");
      var skipNote = el("span", "td-upsell-popup__skip-note");
      skipNote.textContent = ENTRY.skip_note || "This deal disappears when you close this window.";
      skipWrap.appendChild(skipNote);
      var decline = el("button", "td-upsell-popup__decline", { type: "button" });
      decline.textContent = ENTRY.decline_label || "Skip the offer";
      decline.addEventListener("click", function () {
        dismiss("decline");
      });
      skipWrap.appendChild(decline);
      modal.appendChild(skipWrap);

      overlay.appendChild(modal);
      overlay.addEventListener("click", function (e) {
        if (e.target === overlay) dismiss("overlay");
      });
      overlay.addEventListener("keydown", onKeydown);
      document.body.appendChild(overlay);
    }

    // Optional top bar. Renders only if header_text or a positive
    // countdown_minutes is present. The countdown is VISUAL ONLY (see
    // startCountdown + README §compliance). Returns the bar element, or null.
    function buildTopbar() {
      var hasText = !!(ENTRY.header_text && String(ENTRY.header_text).trim());
      var mins = Number(ENTRY.countdown_minutes);
      var hasTimer = !isNaN(mins) && mins > 0;
      if (!hasText && !hasTimer) return null; // both empty → no bar at all

      var bar = el("div", "td-upsell-popup__topbar");
      if (hasText) {
        var t = el("span", "td-upsell-popup__topbar-text");
        t.textContent = ENTRY.header_text;
        bar.appendChild(t);
      }
      if (hasTimer) {
        countdownEl = el("span", "td-upsell-popup__topbar-timer");
        countdownEl.textContent = fmtClock(mins * 60); // initial static value
        bar.appendChild(countdownEl);
      }
      return bar;
    }

    function fmtClock(sec) {
      if (sec < 0) sec = 0;
      var m = Math.floor(sec / 60);
      var s = sec % 60;
      return m + ":" + (s < 10 ? "0" : "") + s;
    }

    // VISUAL-ONLY countdown. Decoration, never a mechanism: it does NOT gate the
    // discount, change price, or disable the offer. At 00:00 it just stops. A
    // fake resetting expiry timer is an EU Omnibus/UCPD risk — do not wire this
    // to anything. Session start persists across reload + soft nav (same
    // session), resets on a fresh visit. Fails open: any error leaves the bar
    // text and the popup untouched.
    function startCountdown() {
      if (countdownStarted || !countdownEl) return;
      countdownStarted = true;
      try {
        var mins = Number(ENTRY.countdown_minutes);
        if (isNaN(mins) || mins <= 0) return;
        var totalSec = Math.floor(mins * 60);
        var key = "td_upsell_popup_timer_" + ENTRY.handle;
        var start = NaN;
        try {
          start = parseInt(sessionStorage.getItem(key), 10);
        } catch (e) {}
        if (!start || isNaN(start)) {
          start = Date.now();
          try {
            sessionStorage.setItem(key, String(start));
          } catch (e) {}
        }
        function tick() {
          try {
            var elapsed = Math.floor((Date.now() - start) / 1000);
            var rem = totalSec - elapsed;
            if (rem <= 0) {
              countdownEl.textContent = fmtClock(0); // stop AT 00:00; offer stays available
              if (countdownTimer) {
                clearInterval(countdownTimer);
                countdownTimer = null;
              }
              return;
            }
            countdownEl.textContent = fmtClock(rem);
          } catch (e) {
            if (countdownTimer) {
              clearInterval(countdownTimer);
              countdownTimer = null;
            }
          }
        }
        tick();
        countdownTimer = setInterval(tick, 1000);
      } catch (e) {
        // fail open — bar text stays as-is, popup unaffected
      }
    }

    // ---- Bundle support ------------------------------------------------------
    // Build one picker slot per CHOOSABLE bundle_components entry (flavorMap),
    // translating each flavorMap canonical GID through gid_map to this market's
    // GID and matching it against the market flavour pool. Mirrors the proven
    // collection-pack-grid deriveFlavours. Fixed/gift entries carry no flavorMap
    // and are skipped (they ride inside the single bundle variant).
    function deriveBundleSlots(product) {
      var comps = product.bundle_components || [];
      var data = product.bundle_data || {};
      var pool = data.flavours || [];
      var gmap = data.gid_map || {};
      var byGid = {};
      pool.forEach(function (f) {
        byGid[f.gid] = f;
      });
      var slots = [];
      comps.forEach(function (c) {
        if (!c || !c.flavorMap) return; // fixed/gift → no choice
        var opts = [];
        var seen = {};
        var productName = "";
        Object.keys(c.flavorMap).forEach(function (name) {
          var canonGid = c.flavorMap[name];
          var marketGid = gmap[canonGid] || canonGid; // substitute for this market
          var f = byGid[marketGid];
          if (!f) return; // not sold in this market → drop
          if (seen[marketGid]) return; // two names collapse to one → dedupe
          seen[marketGid] = 1;
          if (!productName && f.product) productName = f.product;
          opts.push({ id: f.id, gid: f.gid, title: f.title, thumb: f.image, available: f.available !== false, low: f.low === true });
        });
        if (opts.length) slots.push({ product: productName, options: opts });
      });
      return slots;
    }

    // Low-stock warning (theme snippet td-stock-warn sets v.low). A warning only:
    // low flavours stay pickable. The popup shows the alert under the picker but no
    // tag in the dropdown (the rows are too narrow for it). The alert names every
    // low pick, so a bundle card with two low slots shows one alert, not two.
    function makeLowAlert() {
      var box = el("div", "td-upsell-popup__low-alert", { role: "status" });
      box.style.display = "none";
      var icon = el("img", "td-upsell-popup__low-alert__icon", { alt: "", width: "18", height: "18" });
      if (payload && payload.low_icon) icon.src = payload.low_icon;
      var text = el("span", "td-upsell-popup__low-alert__text");
      box.appendChild(icon);
      box.appendChild(text);
      return {
        el: box,
        update: function (picks) {
          var names = [];
          (picks || []).forEach(function (p) {
            if (p && p.low && p.title && names.indexOf(p.title) < 0) names.push(p.title);
          });
          if (!names.length) {
            box.style.display = "none";
            return;
          }
          var tpl = (payload && payload.low_alert) || "{flavour} is very low in stock: delivery may take longer than expected.";
          text.textContent = tpl.split("{flavour}").join(names.join(", "));
          box.style.display = "";
        }
      };
    }

    // One portaled flavour picker for a bundle slot. Returns { wrap, get, close }.
    // Self-contained (mirrors the normal card picker) so the normal path stays
    // untouched. Only used by buildBundleCard.
    function makeBundleSlot(options, card, onChange) {
      var selected = options[0];
      var wrap = el("div", "td-upsell-popup__flavor-wrap");
      var sel = el("div", "td-upsell-popup__flavor-select", {
        role: "button",
        tabindex: "0",
        "aria-haspopup": "listbox",
        "aria-expanded": "false"
      });
      var selImg = el("img", "td-upsell-popup__flavor-select__img", { alt: "" });
      function applyThumb(imgEl, v) {
        if (v && v.thumb) {
          imgEl.src = v.thumb;
          imgEl.style.display = "";
        } else {
          imgEl.removeAttribute("src");
          imgEl.style.display = "none";
        }
      }
      applyThumb(selImg, selected);
      var selTextWrap = el("span", "td-upsell-popup__flavor-select__text");
      var selTextVal = el("span");
      selTextVal.textContent = selected.title || "";
      selTextWrap.appendChild(selTextVal);
      sel.appendChild(selImg);
      sel.appendChild(selTextWrap);
      var multi = options.length > 1;
      if (multi) sel.insertAdjacentHTML("beforeend", svgArrow());
      else sel.classList.add("is-static");
      wrap.appendChild(sel);

      var dropdown = el("div", "td-upsell-popup__flavor-dropdown", { role: "listbox" });
      options.forEach(function (v, i) {
        var opt = el("button", "td-upsell-popup__flavor-option" + (i === 0 ? " is-active" : ""), {
          type: "button",
          role: "option",
          "aria-selected": i === 0 ? "true" : "false"
        });
        if (v.thumb) {
          var oImg = el("img", "td-upsell-popup__flavor-option__img", { alt: "" });
          oImg.src = v.thumb;
          opt.appendChild(oImg);
        }
        var oText = document.createElement("span");
        oText.textContent = v.title || "";
        opt.appendChild(oText);
        opt.addEventListener("click", function (e) {
          e.stopPropagation();
          selected = v;
          if (onChange) onChange();
          dropdown.querySelectorAll(".td-upsell-popup__flavor-option").forEach(function (o) {
            o.classList.remove("is-active");
            o.setAttribute("aria-selected", "false");
          });
          opt.classList.add("is-active");
          opt.setAttribute("aria-selected", "true");
          selTextVal.textContent = v.title || "";
          applyThumb(selImg, v);
          closeDropdown();
        });
        dropdown.appendChild(opt);
      });
      wrap.appendChild(dropdown);

      function positionDropdown() {
        var r = sel.getBoundingClientRect();
        dropdown.style.position = "fixed";
        dropdown.style.width = r.width + "px";
        dropdown.style.left = r.left + "px";
        var dh = dropdown.offsetHeight;
        var below = window.innerHeight - r.bottom;
        if (below < dh + 8 && r.top > below) {
          dropdown.style.top = Math.max(8, r.top - dh - 4) + "px";
        } else {
          dropdown.style.top = r.bottom + 4 + "px";
        }
      }
      function onDdReposition(e) {
        var t = e && e.target;
        if (t && (t === dropdown || (t.nodeType === 1 && dropdown.contains(t)))) return;
        var r = sel.getBoundingClientRect();
        if (r.bottom <= 0 || r.top >= window.innerHeight) closeDropdown();
        else positionDropdown();
      }
      function openDropdown() {
        if (!multi) return;
        closeAllDropdowns();
        document.body.appendChild(dropdown);
        dropdown.classList.add("is-open");
        sel.classList.add("is-open");
        sel.setAttribute("aria-expanded", "true");
        positionDropdown();
        window.addEventListener("scroll", onDdReposition, true);
        window.addEventListener("resize", onDdReposition);
      }
      function closeDropdown() {
        sel.classList.remove("is-open");
        sel.setAttribute("aria-expanded", "false");
        dropdown.classList.remove("is-open");
        dropdown.style.position = "";
        dropdown.style.top = "";
        dropdown.style.left = "";
        dropdown.style.width = "";
        if (dropdown.parentNode === document.body) wrap.appendChild(dropdown);
        window.removeEventListener("scroll", onDdReposition, true);
        window.removeEventListener("resize", onDdReposition);
      }
      sel.addEventListener("click", function (e) {
        e.stopPropagation();
        if (!multi) return;
        if (sel.classList.contains("is-open")) closeDropdown();
        else openDropdown();
      });
      sel.addEventListener("keydown", function (e) {
        if (!multi) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          if (sel.classList.contains("is-open")) closeDropdown();
          else openDropdown();
        }
      });
      return {
        wrap: wrap,
        get: function () {
          return selected;
        },
        close: closeDropdown
      };
    }

    function buildBundleCard(product) {
      var slots = deriveBundleSlots(product);
      var card = el("div", "td-upsell-popup__card td-upsell-popup__card--bundle");

      var imgWrap = el("div", "td-upsell-popup__card-img");
      var img = el("img", null, { alt: product.title || "", loading: "lazy" });
      img.src = product.image_override || product.image || "";
      imgWrap.appendChild(img);
      card.appendChild(imgWrap);

      var disc = el("span", "td-upsell-popup__card-discount");
      disc.style.display = "none";
      card.appendChild(disc);

      var info = el("div", "td-upsell-popup__card-info");

      var name = el("div", "td-upsell-popup__card-name");
      name.textContent = product.title || "";
      info.appendChild(name);

      var priceRow = el("div", "td-upsell-popup__card-price");
      var now = el("span", "td-upsell-popup__card-price-now");
      now.textContent = product.price_money || "";
      var was = el("span", "td-upsell-popup__card-price-was");
      was.style.display = "none";
      priceRow.appendChild(now);
      priceRow.appendChild(was);
      info.appendChild(priceRow);
      // Bundle price is fixed (the bundle variant carries it) — set once, no per-pick change.
      if (product.on_sale && product.compare_at_money) {
        was.textContent = product.compare_at_money;
        was.style.display = "";
        var pc = pct(product.compare_at_price, product.price);
        if (pc) {
          disc.textContent = (payload && payload.save_label || "SAVE") + " " + pc + "%";
          disc.style.display = "";
        }
      }

      // One picker per choosable slot, headed with that slot's product name
      // (e.g. "Energy 2.0") so the customer knows which flavour is for what.
      var pickers = [];
      slots.forEach(function (slot) {
        if (slot.product) {
          var slotLabel = el("div", "td-upsell-popup__flavor-label");
          slotLabel.textContent = slot.product;
          info.appendChild(slotLabel);
        }
        var picker = makeBundleSlot(slot.options, card, syncLow);
        pickers.push(picker);
        info.appendChild(picker.wrap);
      });
      var lowAlert = makeLowAlert();
      info.appendChild(lowAlert.el);
      function syncLow() {
        lowAlert.update(pickers.map(function (p) {
          return p.get();
        }));
      }
      syncLow();

      var add = el("button", "td-upsell-popup__card-add", { type: "button" });
      add.textContent = ENTRY.cta_label || "Add to my order";
      add.addEventListener("click", function () {
        var picks = pickers.map(function (p) {
          var o = p.get();
          return { title: o.title, gid: o.gid };
        });
        // Synthetic variant: the ONE bundle variant + the chosen flavours (ride
        // along as line-item properties). addUpsellLine reads _bundlePicks.
        var variant = {
          id: product.bundle_variant_id,
          price: product.price,
          _bundle: true,
          _bundlePicks: picks
        };
        track("click", { variant_id: product.bundle_variant_id, value: product.price });
        accept(add, variant);
      });
      info.appendChild(add);

      card.appendChild(info);
      card._closeDropdown = function () {
        pickers.forEach(function (p) {
          p.close();
        });
      };
      return card;
    }

    // Order flavour options by the product's `tastydose.flavor_picker_order` (a
    // comma-separated SKU list, locale-proof — same metafield the product page uses).
    // The FIRST entry becomes the prefilled/default flavour. Options not listed keep
    // their natural order, appended after. No order set → unchanged.
    function orderVariants(list, order) {
      if (!order || !list || !list.length) return list;
      var seq = String(order)
        .split(",")
        .map(function (s) {
          return s.trim();
        })
        .filter(Boolean);
      if (!seq.length) return list;
      var bySku = {};
      list.forEach(function (v) {
        if (v && v.sku && bySku[v.sku] == null) bySku[v.sku] = v;
      });
      var out = [];
      var used = {};
      seq.forEach(function (sku) {
        var v = bySku[sku];
        if (v && !used[v.id]) {
          out.push(v);
          used[v.id] = 1;
        }
      });
      list.forEach(function (v) {
        if (v && !used[v.id]) {
          out.push(v);
          used[v.id] = 1;
        }
      });
      return out;
    }

    // Per-slot prefill: pick the variant whose SKU matches the slot's `offer_default_N`
    // (metaobject field). Lets two slots of the SAME product default to different
    // flavours (e.g. slot 1 = Classic Coffee, slot 2 = Vanilla). Falls back to the
    // first option (picker-order aware) when unset or not found.
    function defaultVariant(list, sku) {
      if (!list || !list.length) return list && list[0];
      // Prefer the requested SKU, but never prefill an out-of-stock (<=5) flavour.
      if (sku) {
        var s = String(sku).trim();
        for (var i = 0; i < list.length; i++) {
          if (list[i] && list[i].sku === s && list[i].available !== false) return list[i];
        }
      }
      for (var j = 0; j < list.length; j++) {
        if (list[j] && list[j].available !== false) return list[j];
      }
      return list[0];
    }

    function buildCard(product) {
      if (product && product.bundle) return buildBundleCard(product);
      var variants = orderVariants((product.variants || []).slice(), product.picker_order);
      var selected = defaultVariant(variants, product.default_sku);

      var card = el("div", "td-upsell-popup__card");

      var imgWrap = el("div", "td-upsell-popup__card-img");
      var img = el("img", null, { alt: product.title || "", loading: "lazy" });
      img.src = imageFor(selected, product);
      imgWrap.appendChild(img);
      card.appendChild(imgWrap);
      // SAVE badge sits OVER the image but is a child of the card (not the image
      // wrapper), so the image's overflow:hidden can't clip it. renderPrice() toggles it.
      var disc = el("span", "td-upsell-popup__card-discount");
      disc.style.display = "none";
      card.appendChild(disc);

      var info = el("div", "td-upsell-popup__card-info");

      var name = el("div", "td-upsell-popup__card-name");
      name.textContent = product.title || "";
      info.appendChild(name);

      var priceRow = el("div", "td-upsell-popup__card-price");
      var now = el("span", "td-upsell-popup__card-price-now");
      var was = el("span", "td-upsell-popup__card-price-was");
      priceRow.appendChild(now);
      priceRow.appendChild(was);
      info.appendChild(priceRow);

      function renderPrice(v) {
        now.textContent = v.price_money || "";
        if (v.on_sale && v.compare_at_money) {
          was.textContent = v.compare_at_money;
          was.style.display = "";
          var p = pct(v.compare_at_price, v.price);
          if (p) {
            disc.textContent = (payload && payload.save_label || "SAVE") + " " + p + "%";
            disc.style.display = "";
          } else {
            disc.style.display = "none";
          }
        } else {
          was.style.display = "none";
          disc.style.display = "none";
        }
      }
      renderPrice(selected);

      // "Select flavour:" label above the dropdown (was option_name inside the
      // select). Localized — reads the market's option name (e.g. Flavour/Geschmack).
      if (product.option_name) {
        var flavLabel = el("div", "td-upsell-popup__flavor-label");
        flavLabel.textContent = product.option_name;
        info.appendChild(flavLabel);
      }

      // Dropdown thumbnail = per-variant flavour icon (v.thumb). null → no image.
      function applyThumb(imgEl, v) {
        if (v && v.thumb) {
          imgEl.src = v.thumb;
          imgEl.style.display = "";
        } else {
          imgEl.removeAttribute("src");
          imgEl.style.display = "none";
        }
      }

      var wrap = el("div", "td-upsell-popup__flavor-wrap");
      var sel = el("div", "td-upsell-popup__flavor-select", {
        role: "button",
        tabindex: "0",
        "aria-haspopup": "listbox",
        "aria-expanded": "false"
      });
      var selImg = el("img", "td-upsell-popup__flavor-select__img", { alt: "" });
      applyThumb(selImg, selected);
      var selTextWrap = el("span", "td-upsell-popup__flavor-select__text");
      var selTextVal = el("span");
      selTextVal.textContent = selected.title || "";
      selTextWrap.appendChild(selTextVal);
      sel.appendChild(selImg);
      sel.appendChild(selTextWrap);

      var multi = variants.length > 1;
      if (multi) sel.insertAdjacentHTML("beforeend", svgArrow());
      else sel.classList.add("is-static");
      wrap.appendChild(sel);

      var dropdown = el("div", "td-upsell-popup__flavor-dropdown", { role: "listbox" });
      var soldOutLabel = (payload && payload.soldout_label) || "Out of stock";
      variants.forEach(function (v) {
        var oos = v.available === false;                 // stock <= 5 → not pickable
        var isSel = !oos && selected && v.id === selected.id;
        var opt = el(
          "button",
          "td-upsell-popup__flavor-option" + (isSel ? " is-active" : "") + (oos ? " is-unavailable" : ""),
          {
            type: "button",
            role: "option",
            "aria-selected": isSel ? "true" : "false",
            "aria-disabled": oos ? "true" : "false"
          }
        );
        if (oos) opt.disabled = true;
        if (v.thumb) {
          var oImg = el("img", "td-upsell-popup__flavor-option__img", { alt: "" });
          oImg.src = v.thumb;
          opt.appendChild(oImg);
        }
        var oText = document.createElement("span");
        oText.textContent = (v.title || "") + (oos ? " — " + soldOutLabel : "");
        opt.appendChild(oText);
        if (!oos) {
          opt.addEventListener("click", function (e) {
            e.stopPropagation();
            selected = v;
            dropdown.querySelectorAll(".td-upsell-popup__flavor-option").forEach(function (o) {
              o.classList.remove("is-active");
              o.setAttribute("aria-selected", "false");
            });
            opt.classList.add("is-active");
            opt.setAttribute("aria-selected", "true");
            selTextVal.textContent = v.title || "";
            applyThumb(selImg, v);
            img.src = imageFor(v, product);
            renderPrice(v);
            lowAlert.update([v]);
            closeDropdown();
          });
        }
        dropdown.appendChild(opt);
      });
      wrap.appendChild(dropdown);

      // The open list is PORTALED to document.body as position:fixed so the
      // modal's overflow/transform + overlay's backdrop-filter can't clip it.
      function positionDropdown() {
        var r = sel.getBoundingClientRect();
        dropdown.style.position = "fixed";
        dropdown.style.width = r.width + "px";
        dropdown.style.left = r.left + "px";
        var dh = dropdown.offsetHeight;
        var below = window.innerHeight - r.bottom;
        if (below < dh + 8 && r.top > below) {
          dropdown.style.top = Math.max(8, r.top - dh - 4) + "px"; // flip above
        } else {
          dropdown.style.top = r.bottom + 4 + "px";
        }
      }
      function onDdReposition(e) {
        // Ignore the dropdown's OWN internal scroll (long flavour lists scroll
        // inside their max-height). Capture-phase window listener, so a scroll
        // inside the portaled dropdown reaches it too — without this guard,
        // scrolling the list would close the picker.
        var t = e && e.target;
        if (t && (t === dropdown || (t.nodeType === 1 && dropdown.contains(t)))) return;
        // Page/modal scroll OR viewport resize (mobile address-bar show/hide fires
        // resize with target=window). Don't close mid-selection — re-anchor the
        // picker to its trigger and keep it open. Close only once the trigger has
        // scrolled out of view.
        var r = sel.getBoundingClientRect();
        if (r.bottom <= 0 || r.top >= window.innerHeight) {
          closeDropdown();
        } else {
          positionDropdown();
        }
      }
      function openDropdown() {
        if (!multi) return;
        closeAllDropdowns();
        document.body.appendChild(dropdown); // escape modal clipping
        dropdown.classList.add("is-open");
        sel.classList.add("is-open");
        sel.setAttribute("aria-expanded", "true");
        positionDropdown();
        window.addEventListener("scroll", onDdReposition, true);
        window.addEventListener("resize", onDdReposition);
      }
      function closeDropdown() {
        sel.classList.remove("is-open");
        sel.setAttribute("aria-expanded", "false");
        dropdown.classList.remove("is-open");
        dropdown.style.position = "";
        dropdown.style.top = "";
        dropdown.style.left = "";
        dropdown.style.width = "";
        if (dropdown.parentNode === document.body) wrap.appendChild(dropdown); // re-parent back
        window.removeEventListener("scroll", onDdReposition, true);
        window.removeEventListener("resize", onDdReposition);
      }
      sel.addEventListener("click", function (e) {
        e.stopPropagation();
        if (!multi) return;
        if (sel.classList.contains("is-open")) closeDropdown();
        else openDropdown();
      });
      sel.addEventListener("keydown", function (e) {
        if (!multi) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          if (sel.classList.contains("is-open")) closeDropdown();
          else openDropdown();
        }
      });
      card._closeDropdown = closeDropdown;

      info.appendChild(wrap);
      var lowAlert = makeLowAlert();
      info.appendChild(lowAlert.el);
      lowAlert.update([selected]);

      var add = el("button", "td-upsell-popup__card-add", { type: "button" });
      add.textContent = ENTRY.cta_label || "Add to my order";
      add.addEventListener("click", function () {
        track("click", { variant_id: selected.id, value: selected.price });
        accept(add, selected);
      });
      info.appendChild(add);

      card.appendChild(info);
      return card;
    }

    function closeAllDropdowns() {
      if (!overlay) return;
      overlay.querySelectorAll(".td-upsell-popup__card").forEach(function (c) {
        if (typeof c._closeDropdown === "function") c._closeDropdown();
      });
    }
    document.addEventListener("click", closeAllDropdowns);

    // ---- Cart integration ---------------------------------------------------
    var tdPopupAdding = false; // popup's own add
    // Layer C: an add made from INSIDE the cart drawer (its upsell rail). The
    // drawer is already open and showing an offer; a popup on top of it is the
    // bug Tim reported. The drawer sets this global around its own /cart/add.js,
    // so a landing-page or PDP add still fires the popup normally.
    function drawerAdding() {
      return !!window.__tdSuppressUpsellPopup;
    }
    var resumeSuppress = false; // Layer A: suppress our triggers during resume
    var lastAddResponse = null; // /cart/add.js JSON of our last upsell add (custom-cart mode)
    var gatedByAdd = false; // add_to_cart trigger: hold the cart drawer shut while the popup is up
    var cartGateTimer = null; // brief watchdog that re-closes the drawer if the add reopens it
    var lastAddedParentHandle = null; // product handle of the add that triggered us (for _upsell_for)

    // Which drawer to drive after an add. "upcart" (default) = the live UpCart app;
    // "custom" = the theme's native <cart-drawer> element (Dawn-style renderContents).
    // Set via the "Cart drawer" theme setting → payload.cart_mode.
    function isCustomCart() {
      return !!(payload && payload.cart_mode === "custom");
    }

    // Refresh + open the theme's native <cart-drawer>. If our add response carries
    // rendered sections (we request them in custom mode), renderContents updates the
    // drawer AND opens it; otherwise fall back to open().
    function openCustomCart() {
      var drawer = document.querySelector("cart-drawer");
      if (!drawer) return;
      try {
        if (lastAddResponse && lastAddResponse.sections && typeof drawer.renderContents === "function") {
          drawer.renderContents(lastAddResponse);
        } else if (typeof drawer.open === "function") {
          drawer.open();
        } else {
          drawer.classList.add("animate", "active");
        }
      } catch (e) {
        try {
          if (typeof drawer.open === "function") drawer.open();
        } catch (e2) {}
      }
    }

    // ---- Cart gating (add_to_cart trigger) ----------------------------------
    // On a bundle product the flavour-picker adds first, which auto-opens the cart
    // drawer (UpCart on live: #upCart; the theme's native <cart-drawer> on dev).
    // When armed by add_to_cart we want: picker → flavours → (drawer stays hidden)
    // → popup → accept/close → THEN the drawer shows. We MASK the drawer with CSS
    // while the popup is up (no dependency on UpCart's private open/close API) and
    // reveal it when the popup closes. The drawer still "opened" underneath, so
    // revealing shows it with the freshly added item(s).
    var GATE_STYLE_ID = "td-upsell-cart-gate";
    function cartGateStyle(on) {
      var ex = document.getElementById(GATE_STYLE_ID);
      if (on) {
        if (ex) return;
        var st = document.createElement("style");
        st.id = GATE_STYLE_ID;
        st.textContent =
          "#upCart{opacity:0!important;pointer-events:none!important;}" +
          "cart-drawer.active,cart-drawer[open]{opacity:0!important;pointer-events:none!important;}" +
          "#CartDrawer.active,#CartDrawer.animate{opacity:0!important;pointer-events:none!important;}";
        (document.head || document.documentElement).appendChild(st);
      } else if (ex && ex.parentNode) {
        ex.parentNode.removeChild(ex);
      }
    }
    function startCartGate() {
      gatedByAdd = true;
      cartGateStyle(true);
      if (cartGateTimer) clearInterval(cartGateTimer);
      var ticks = 0;
      cartGateTimer = setInterval(function () {
        cartGateStyle(true);
        if (++ticks > 30) {
          clearInterval(cartGateTimer);
          cartGateTimer = null;
        }
      }, 100);
    }
    function endCartGate() {
      if (!gatedByAdd && !document.getElementById(GATE_STYLE_ID)) return;
      if (cartGateTimer) {
        clearInterval(cartGateTimer);
        cartGateTimer = null;
      }
      gatedByAdd = false;
      cartGateStyle(false); // reveal the drawer (already open underneath)
    }

    // The flavour picker is a top-layer <dialog> (showModal) — it renders ABOVE any
    // z-index, so our popup would stay hidden behind it until the theme's own slow
    // closePicker() (add + sections fetch, ~1-2s). Close the picker dialog ourselves
    // the instant we fire, so the popup replaces it seamlessly (the add completes in
    // the background). Never touches our own overlay (a div, not a dialog).
    function closePickerDialogs() {
      try {
        var dialogs = document.querySelectorAll("dialog[open]");
        for (var i = 0; i < dialogs.length; i++) {
          var d = dialogs[i];
          if (d === overlay) continue;
          if (typeof d.close === "function") d.close();
          else d.removeAttribute("open");
        }
      } catch (e) {}
    }

    // UpCart embed off → the theme's native <cart-drawer> is the cart, and the
    // "upcart" mode below only nudges an app that is not there: the upsell line
    // landed in the cart but the drawer kept showing the pre-upsell contents.
    // Refresh the native drawer through the shared helper instead. Where UpCart
    // is still enabled (live) this never runs.
    function refreshNativeDrawer(open) {
      if (typeof window.upcartRefreshCart === "function") return false;
      if (typeof window.tdRefreshCartDrawer !== "function") return false;
      try { window.tdRefreshCartDrawer(open); } catch (e) {}
      return true;
    }

    function refreshCart() {
      if (isCustomCart()) {
        openCustomCart();
        return;
      }
      if (refreshNativeDrawer(true)) return;
      try {
        if (typeof window.upcartRefreshCart === "function") window.upcartRefreshCart();
      } catch (e) {}
      try {
        if (typeof window.upcartUpdateCart === "function") window.upcartUpdateCart();
      } catch (e) {}
      ["cart:refresh", "cart:updated"].forEach(function (ev) {
        document.dispatchEvent(new CustomEvent(ev, { bubbles: true }));
      });
    }

    // Quiet in-place refresh for the main-first (click-intercept) path. UpCart is
    // ALREADY open from the main add's native auto-open and re-renders the upsell
    // add on its own; we only nudge its contents. Deliberately NO upcartUpdateCart
    // and NO cart:* event dispatches here — those, firing while UpCart animates
    // open, are what close/reopen the drawer (the flicker). upsell-blocker.js and
    // td-upsell-cascade.js still catch the add via their own fetch/XHR hooks +
    // 1200ms poll, so their re-evaluation is unaffected.
    function refreshCartInPlace() {
      if (isCustomCart()) {
        openCustomCart();
        return;
      }
      if (refreshNativeDrawer(true)) return;
      try {
        if (typeof window.upcartRefreshCart === "function") window.upcartRefreshCart();
      } catch (e) {}
    }

    function applyDiscount(code) {
      if (!code) return Promise.resolve();
      return fetch("/discount/" + encodeURIComponent(code), { method: "GET", credentials: "same-origin" }).catch(function () {});
    }

    // Reporting for BUNDLE upsells. The Cart Transform that splits a bundle into
    // its items at checkout drops every line property set in addUpsellLine, so
    // the order never learns which popup sold the bundle. Cart attributes DO
    // survive into the order (customAttributes / note_attributes), so record the
    // source there, keyed by the bundle variant id. One entry per popup add,
    // " || "-joined. td-upsell-cascade.js removes the attribute again when that
    // variant leaves the cart. Fire-and-forget: never blocks or fails the add.
    function recordBundleSource(variant) {
      if (!variant || !variant._bundle || !variant.id) return;
      var key = "_upsell_bundle_" + Number(variant.id);
      var entry = "placement=popup;id=" + ENTRY.handle + ";market=" + COUNTRY + ";page=" + PAGE_HANDLE;
      if (ENTRY.ab_variant) entry += ";ab=" + ENTRY.ab_variant;
      fetch("/cart.js", { headers: { Accept: "application/json" }, credentials: "same-origin" })
        .then(function (r) {
          return r.json();
        })
        .then(function (cart) {
          var prev = (cart && cart.attributes && cart.attributes[key]) || "";
          var attrs = {};
          attrs[key] = prev ? prev + " || " + entry : entry;
          return fetch("/cart/update.js", {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            credentials: "same-origin",
            body: JSON.stringify({ attributes: attrs })
          });
        })
        .catch(function () {});
    }

    // Add OUR upsell line via /cart/add.js. Returns the fetch promise (resolved
    // to JSON on success, throws on !ok). Guarded elsewhere by tdPopupAdding.
    function addUpsellLine(variant) {
      var props = {
        _upsell_placement: "popup",
        _upsell_id: ENTRY.handle,
        _upsell_market: COUNTRY,
        _upsell_page: PAGE_HANDLE
      };
      if (ENTRY.ab_variant) props._upsell_ab = ENTRY.ab_variant;
      // Anti-scam link: tie this upsell line to the parent (the bundle whose add
      // triggered us). td-upsell-cascade.js removes any upsell line whose
      // `_upsell_for` handle is no longer in the cart — so removing the bundle
      // auto-removes the upsell. Only set on the add_to_cart flow (parent captured);
      // click/delay stay unlinked = old safe behaviour.
      if (lastAddedParentHandle) props._upsell_for = lastAddedParentHandle;
      // Marks the bundle line in the CART only. The Cart Transform drops all of
      // these properties when it splits the bundle at checkout; the order-side
      // record is the cart attribute written by recordBundleSource() below.
      if (variant && variant._bundle) props._upsell_type = "bundle-popup-upsell";
      // Bundle: the chosen flavours ride as line-item properties on the ONE bundle
      // variant (Flavors = display names, _FlavorIds = this-market variant GIDs) —
      // same contract the collection pack grid uses.
      if (variant && variant._bundlePicks && variant._bundlePicks.length) {
        props.Flavors = variant._bundlePicks
          .map(function (p) {
            return p.title;
          })
          .join(", ");
        props._FlavorIds = variant._bundlePicks
          .map(function (p) {
            return p.gid;
          })
          .join(",");
      }
      var payloadBody = {
        items: [
          {
            id: Number(variant.id),
            quantity: 1,
            properties: props
          }
        ]
      };
      // Custom-cart mode: ask /cart/add.js to render the drawer sections back so the
      // native <cart-drawer> can update + open with the new line in one round-trip.
      if (isCustomCart()) {
        payloadBody.sections = "cart-drawer,cart-icon-bubble";
        payloadBody.sections_url = window.location.pathname;
      }
      return fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payloadBody)
      }).then(function (r) {
        if (!r.ok) throw new Error("upsell add failed " + r.status);
        return r.json();
      }).then(function (data) {
        lastAddResponse = data; // consumed by openCustomCart()
        recordBundleSource(variant);
        return data;
      });
    }

    function finishUpsell(variant, refreshFn) {
      track("accept", { variant_id: variant.id, value: variant.price });
      return applyDiscount(ENTRY.discount_code).then(function () {
        (refreshFn || refreshCart)();
        tdPopupAdding = false;
        close();
      });
    }

    function accept(button, variant) {
      if (!variant || tdPopupAdding) return;
      tdPopupAdding = true;
      var original = button.textContent;
      button.disabled = true;

      // A click-intercept on a real BUTTON must land the MAIN item first (the
      // button carries Flavors/_FlavorIds/bundle we can't rebuild), so the cart
      // never passes through an upsell-only state (which flashes upsell-blocker's
      // toast). Anchors (navigation) and passive triggers keep the upsell-first
      // path.
      var isButtonIntercept = false;
      try {
        isButtonIntercept = !!(pendingTarget && (!pendingTarget.closest || !pendingTarget.closest("a[href]")));
      } catch (e) {
        isButtonIntercept = false;
      }

      if (isButtonIntercept) acceptMainFirst(button, variant);
      else acceptUpsellFirst(button, variant, original);
    }

    // Passive triggers + anchor intercepts: add the upsell, then resume (no-op
    // for passive; navigation for anchors).
    function acceptUpsellFirst(button, variant, original) {
      addUpsellLine(variant)
        .then(function () {
          return finishUpsell(variant);
        })
        .then(function () {
          resume();
        })
        .catch(function (e) {
          log("add error", e);
          failsafeEvent("accept_add_failed");
          tdPopupAdding = false;
          if (pendingTarget) {
            close();
            resume(); // fail open — proceed with the original action
          } else {
            button.disabled = false;
            button.textContent = original; // passive → allow retry
          }
        });
    }

    // Click-intercept on a button: resume() first so the REAL button adds the
    // MAIN line (with its full payload); capture that add's /cart/add.js with a
    // one-shot fetch wrapper; only once it RESOLVES ok do we add the upsell.
    // Cart path: empty → main → main+upsell. Never upsell-only.
    function acceptMainFirst(button, variant) {
      var settled = false;
      var captured = false;
      var timeoutId = null;
      var chainFetch = window.fetch; // exact current top of the fetch chain
      var oneShotActive = true;

      function disarmWrapper() {
        oneShotActive = false;
        // Only unwind if nothing wrapped on top of us since (never clobber a
        // newer wrapper — go inert instead).
        if (window.fetch === wrapped) window.fetch = chainFetch;
      }
      function finish(fn) {
        if (settled) return;
        settled = true;
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        disarmWrapper();
        fn();
      }
      function mainFailed() {
        // Fail open: the button already ran its own flow/UI. Add NO orphan upsell.
        failsafeEvent("main_add_failed");
        tdPopupAdding = false;
        close();
      }
      function onMainResolved(res) {
        finish(function () {
          if (res && res.ok) {
            addUpsellLine(variant)
              .then(function () {
                // Quiet refresh — UpCart already opened on the main add; avoid a
                // second open (the flicker).
                return finishUpsell(variant, refreshCartInPlace);
              })
              .catch(function (e) {
                log("upsell add error", e);
                failsafeEvent("accept_add_failed");
                tdPopupAdding = false;
                close(); // main already landed — safe, no orphan
              });
          } else {
            mainFailed();
          }
        });
      }

      var wrapped = function () {
        var a0 = arguments[0];
        var url = (a0 && (a0.url || a0)) || "";
        var pr = chainFetch.apply(this, arguments);
        // First /cart/add.js after resume() = the button's MAIN add. A stray
        // non-add fetch (pixel/analytics) does not match, so the one-shot is not
        // consumed. We inspect res.ok only — never read the body.
        if (oneShotActive && !captured && typeof url === "string" && /\/cart\/add(\.js)?/.test(url)) {
          captured = true;
          disarmWrapper(); // got the button's add — restore fetch immediately
          pr.then(onMainResolved).catch(function () {
            finish(mainFailed);
          });
        }
        return pr;
      };
      window.fetch = wrapped;

      // Safety timeout — the button may issue NO add (invalid selection → early
      // return). Fully disarms the wrapper so a late/slow main add cannot trigger
      // a stale upsell; main landing late is fine (no orphan).
      timeoutId = setTimeout(function () {
        finish(mainFailed);
      }, 8000);

      resume(); // re-dispatch the button → MAIN add (resumeSuppress + fired guard our triggers)
    }

    // ---- Open / close -------------------------------------------------------
    var FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    var shown = false;

    function onKeydown(e) {
      if (e.key === "Escape") {
        e.preventDefault();
        dismiss("escape");
        return;
      }
      if (e.key !== "Tab") return;
      var nodes = Array.prototype.filter.call(overlay.querySelectorAll(FOCUSABLE), function (n) {
        return n.getClientRects().length > 0 && !n.disabled;
      });
      if (!nodes.length) return;
      var first = nodes[0];
      var last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    // Fit-to-viewport scale. The modal is a fixed 620px-wide layout whose
    // height depends on the offer — two cards, and the taller one may carry
    // two flavour selects — so on a laptop it can outgrow the screen. Rather
    // than re-flowing it (capping the card image crops the pack photo), the
    // whole popup is scaled down uniformly by exactly as much as it takes.
    // transform does not affect layout, so offsetHeight is always the natural,
    // unscaled height and this never feeds back on itself.
    var fitBound = false;
    function fitScale() {
      if (!overlay) return;
      var m = overlay.querySelector(".td-upsell-popup__modal");
      if (!m) return;
      // Mobile has its own rules and scrolls; leave it alone.
      if (window.innerWidth <= 600) {
        m.style.removeProperty("--tdup-fit");
        return;
      }
      var natural = m.offsetHeight;
      if (!natural) return;
      var room = window.innerHeight - 32; // the overlay's 20px padding, both sides
      var s = natural > room ? room / natural : 1;
      if (s < 0.6) s = 0.6; // floor — below this it stops being readable
      m.style.setProperty("--tdup-fit", String(Math.round(s * 1000) / 1000));
    }

    function open() {
      if (!popupUsable || !overlay || !overlay.parentNode) return false;
      if (shown) return true;
      shown = true;
      prevFocus = document.activeElement;
      document.body.style.overflow = "hidden";
      overlay.removeAttribute("hidden");
      overlay.offsetHeight; // reflow so the transition runs
      overlay.classList.add("is-open");
      // Measure and scale before anything else paints. Guarded: a failure here
      // must never stop the popup from opening.
      try {
        fitScale();
        if (!fitBound) {
          fitBound = true;
          window.addEventListener("resize", fitScale);
          // The free-shipping bar arrives async and makes the modal taller;
          // re-measure whenever the layout height actually changes.
          if (window.ResizeObserver) {
            var fitTarget = overlay.querySelector(".td-upsell-popup__modal");
            if (fitTarget) new ResizeObserver(fitScale).observe(fitTarget);
          }
        }
      } catch (e) {}
      startCountdown(); // visual-only; fails open, independent of the popup
      try {
        updateFreeShipping(); // async, hidden until priced; never blocks the popup
      } catch (e) {}
      // Impression count, frequency-cap record, and focus are deferred to
      // confirmVisible() — only once the popup is verified ACTUALLY shown.
      return true;
    }

    function close() {
      closeAllDropdowns(); // re-parent any portaled dropdown back before hiding
      if (countdownTimer) {
        clearInterval(countdownTimer);
        countdownTimer = null;
      }
      if (!overlay) return;
      shown = false;
      endCartGate(); // reveal the cart drawer the add_to_cart gate was masking
      overlay.classList.remove("is-open");
      overlay.setAttribute("hidden", "");
      document.body.style.overflow = "";
      if (prevFocus && typeof prevFocus.focus === "function") {
        try {
          prevFocus.focus({ preventScroll: true });
        } catch (e) {
          try {
            prevFocus.focus();
          } catch (e2) {}
        }
      }
      // Re-arm for the NEXT add ONLY when the entry opts in via the `refire` toggle
      // (metaobject field). Default (no refire) = one-shot per page load: fires once,
      // re-fires on refresh — the safe behaviour every normal popup keeps. Only the
      // add_to_cart flow re-arms. Per-entry frequency is still re-checked in
      // pickEntryForAdd (isCapped). builtEntry is cleared so the next fire builds a
      // FRESH modal — otherwise the spent Add button stays disabled/"loading".
      if (FIRED_TRIGGER === "add_to_cart" && ENTRY && ENTRY.refire) {
        fired = false;
        confirmedVisible = false;
        dismissed = false;
        resumed = false;
        tdPopupAdding = false;
        gatedByAdd = false;
        lastAddResponse = null;
        lastAddedParentHandle = null;
        builtEntry = null; // force a fresh rebuild on the next fire
        FIRED_TRIGGER = null;
      }
    }

    var dismissed = false;
    function dismiss(reason) {
      if (!dismissed) {
        dismissed = true;
        track("dismiss", { reason: reason });
      }
      close();
      resume(); // no-op unless a click was intercepted
    }

    // ---- Layer 2: is the popup ACTUALLY shown? -----------------------------
    // Uses rect + client rects + computed position:fixed (our modal CSS applied).
    // Does NOT use offsetParent (null for fixed elements) or opacity (mid-transition).
    function isProperlyShown() {
      if (!overlay) return false;
      try {
        if (overlay.getClientRects().length === 0) return false; // display:none
        var r = overlay.getBoundingClientRect();
        if (!(r.width > 0 && r.height > 0)) return false; // zero size
        var cs = window.getComputedStyle(overlay);
        if (cs.display === "none" || cs.visibility === "hidden") return false;
        if (cs.position !== "fixed") return false; // our CSS didn't apply (asset missing)
      } catch (e) {
        return false;
      }
      return true;
    }

    // ==== Triggers ===========================================================
    var fired = false;
    var teardowns = [];
    function addTeardown(fn) {
      teardowns.push(fn);
    }
    function disarmAll() {
      var list = teardowns;
      teardowns = [];
      list.forEach(function (fn) {
        try {
          fn();
        } catch (e) {}
      });
    }
    // ---- Show lifecycle vars (Layers 1–8) ----------------------------------
    var intercepted = false;
    var resumed = false;
    var resuming = false;
    var pendingTarget = null;
    var confirmedVisible = false;
    var watchdogId = null;

    function clearWatchdog() {
      if (watchdogId) {
        clearTimeout(watchdogId);
        watchdogId = null;
      }
    }
    function startWatchdog() {
      clearWatchdog();
      // Layer 3: not confirmed visible within 250ms → treat as failure.
      watchdogId = setTimeout(function () {
        if (!confirmedVisible) failOpen("watchdog");
      }, 250);
    }

    // Remove our nodes + restore page state — "degrade to nothing, not garbage".
    function teardownOverlay() {
      try {
        closeAllDropdowns();
      } catch (e) {}
      // Belt: remove any portaled dropdown still parented to <body>.
      try {
        document.querySelectorAll("body > .td-upsell-popup__flavor-dropdown").forEach(function (d) {
          d.parentNode.removeChild(d);
        });
      } catch (e) {}
      try {
        if (countdownTimer) {
          clearInterval(countdownTimer);
          countdownTimer = null;
        }
      } catch (e) {}
      try {
        shown = false;
      } catch (e) {}
      try {
        document.body.style.overflow = "";
      } catch (e) {}
      try {
        if (overlay && overlay.parentNode) overlay.parentNode.removeChild(overlay);
      } catch (e) {}
    }
    function restoreFocusSafe() {
      if (prevFocus && typeof prevFocus.focus === "function") {
        try {
          prevFocus.focus({ preventScroll: true });
        } catch (e) {
          try {
            prevFocus.focus();
          } catch (e2) {}
        }
      }
    }

    // Success: popup confirmed visible. Only NOW count the impression, burn the
    // frequency cap, and move focus (preventScroll so the page never jumps).
    function confirmVisible() {
      confirmedVisible = true;
      clearWatchdog();
      recordShown(ENTRY);
      track("impression");
      try {
        var c = overlay && overlay.querySelector(".td-upsell-popup__close");
        if (c) c.focus({ preventScroll: true });
      } catch (e) {
        try {
          var c2 = overlay && overlay.querySelector(".td-upsell-popup__close");
          if (c2) c2.focus();
        } catch (e2) {}
      }
    }

    // Any failsafe path: track, disable for session, CLEAN UP (remove nodes +
    // restore scroll/focus) BEFORE resume. resume() is a no-op unless a click
    // was intercepted.
    function failOpen(reason) {
      failsafeEvent(reason);
      sessionDisable();
      clearWatchdog();
      endCartGate(); // popup won't show → reveal the drawer the gate was masking
      teardownOverlay();
      restoreFocusSafe();
      resume();
    }

    // Show + verify visibility. Used by BOTH passive triggers and click intercept.
    function showVerified() {
      // Per-bundle: the add path may have picked a different ENTRY than the one the
      // overlay was built for — rebuild it for the entry we're about to show.
      if (builtEntry !== ENTRY) {
        try {
          teardownOverlay();
        } catch (e) {}
        try {
          buildModal();
        } catch (e) {
          failOpen("build_failed");
          return;
        }
      }
      startWatchdog();
      var opened = false;
      try {
        opened = open();
      } catch (e) {
        opened = false;
      }
      if (!opened) {
        failOpen("open_failed");
        return;
      }
      // Layer 2: verify actually shown; one rAF re-check; else fail open.
      if (isProperlyShown()) {
        confirmVisible();
      } else {
        requestAnimationFrame(function () {
          if (isProperlyShown()) confirmVisible();
          else failOpen("not_visible");
        });
      }
    }

    // Passive fire (delay / exit_intent / add_to_cart / cart_value).
    function fireOnce(name) {
      if (fired) return;
      fired = true;
      FIRED_TRIGGER = name;
      log("fired by", name);
      disarmAll();
      // add_to_cart on a bundle: the flavour picker is a top-layer <dialog> that
      // would hide our popup. Close it now so the popup takes over seamlessly.
      if (name === "add_to_cart") closePickerDialogs();
      showVerified();
    }

    // Layer 8 resume: <a href> → follow href; <button>/other → re-dispatch.
    function resume() {
      clearWatchdog();
      if (resumed) return;
      resumed = true;
      var t = pendingTarget;
      pendingTarget = null;
      if (!t) return; // non-click context — nothing to resume
      try {
        var anchor = t.closest ? t.closest("a[href]") : null;
        if (anchor && anchor.getAttribute("href")) {
          var href = anchor.href;
          if ((anchor.getAttribute("target") || "") === "_blank") window.open(href, "_blank");
          else window.location.assign(href);
          return;
        }
        // Layer A: suppress our own triggers across the resume window.
        resumeSuppress = true;
        resuming = true;
        if (typeof t.click === "function") t.click();
        else t.dispatchEvent(new MouseEvent("click", { bubbles: true, cancelable: true, view: window }));
      } catch (err) {
        resuming = false;
        failsafeEvent("resume_error");
      } finally {
        setTimeout(function () {
          resuming = false;
          resumeSuppress = false;
        }, 0);
      }
    }

    // Resolve which variant a click WOULD add, from the clicked element's OWN
    // context. NEVER a global document query keyed only by tag — a PDP has
    // multiple ATC buttons/forms/bundles and that would read the wrong one.
    // Order: enclosing cart form → data attribute → this theme's pack-selector
    // (walk up to the nearest ancestor holding a checked pack radio) → offer-*
    // global resolver. Returns a positive Number, or null if unresolvable.
    function toVariantNumber(v) {
      var n = Number(v);
      return v != null && !isNaN(n) && n > 0 ? n : null;
    }
    function resolveClickVariant(target) {
      if (!target || !target.closest) return null;

      // 1. Dawn standard: enclosing <form action=/cart/add> input[name="id"].
      try {
        var form = target.closest('form[action*="/cart/add"]');
        if (form) {
          var input = form.querySelector('[name="id"]');
          var fv = input && toVariantNumber(input.value);
          if (fv) return fv;
        }
      } catch (e) {}

      // 2. Data attribute on the button or an ancestor.
      try {
        var dEl = target.closest("[data-variant-id],[data-variant]");
        if (dEl) {
          var dv = toVariantNumber(dEl.getAttribute("data-variant-id") || dEl.getAttribute("data-variant"));
          if (dv) return dv;
        }
      } catch (e) {}

      // 3. This theme's PDP CTA (.product-cta__button). The variant is the
      //    CHECKED pack radio's data-bundle-variant-id. Pack radios carry no
      //    widget-id, so scope by walking UP from the button to the nearest
      //    ancestor that contains a checked pack input — that isolates THIS
      //    bundle even when several bundles share one page.
      try {
        var ctaCtx = target.closest(".product-cta__button, [data-cta-button], [data-product-cta]");
        if (ctaCtx) {
          var scope = ctaCtx;
          while (scope && scope.nodeType === 1) {
            var checked = scope.querySelector ? scope.querySelector("[data-pack-input]:checked") : null;
            if (checked) {
              return toVariantNumber(checked.getAttribute("data-bundle-variant-id")); // may be null → variant_unresolved
            }
            scope = scope.parentElement;
          }
        }
      } catch (e) {}

      // 4. offer-* snippets (#offer-buy-now): variant lives in JS state, exposed
      //    via window.offerGetVariantId(). One offer instance per page.
      try {
        if (target.closest("#offer-buy-now") && typeof window.offerGetVariantId === "function") {
          var ov = toVariantNumber(window.offerGetVariantId());
          if (ov) return ov;
        }
      } catch (e) {}

      return null;
    }

    function armClick() {
      var selector = (ENTRY.trigger_selector || "").trim();
      if (!selector) {
        log("click trigger has no trigger_selector — skipping");
        return;
      }
      if (!popupUsable) {
        log("popup not usable — click trigger not armed"); // Layer 1: never touch the button
        return;
      }
      function handler(e) {
        // Let a resumed (re-dispatched) click pass straight through.
        if (resuming) {
          resuming = false;
          return;
        }
        if (sessionDisabled()) return; // Layers 5/6 at runtime
        if (intercepted || fired) return; // never intercept twice
        // --- Decide WITHOUT suppressing yet. Any doubt → return, click passes.
        var matched;
        try {
          if (!e.target || !e.target.closest) return;
          matched = e.target.closest(selector);
        } catch (err) {
          return;
        }
        if (!matched) return;

        // --- Variant condition (click). Resolve the variant this click WOULD
        //     add from the CLICKED ELEMENT'S OWN CONTEXT — before suppressing
        //     anything. Wrong/unresolvable → return, click passes untouched.
        var wants = ENTRY.trigger_variants;
        if (wants && wants.length) {
          var vid;
          try {
            vid = resolveClickVariant(e.target);
          } catch (err) {
            vid = null;
          }
          if (vid == null) {
            failsafeEvent("variant_unresolved"); // fail open — never touch the click
            return;
          }
          if (wants.indexOf(vid) === -1) return; // wrong variant → no intercept, stay armed
        }

        // --- Earn the intercept: commit, then suppress.
        try {
          intercepted = true;
          fired = true;
          FIRED_TRIGGER = "click";
          disarmAll(); // removes this listener + all other triggers
          pendingTarget = e.target;

          e.preventDefault();
          e.stopImmediatePropagation();

          // Starts watchdog, opens, verifies visibility; on ANY failure it
          // cleans up (removes nodes, restores scroll) and resumes the click.
          showVerified();
        } catch (err) {
          // Any throw after committing → fail open.
          log("click intercept error", err);
          failOpen("error");
        }
      }
      document.addEventListener("click", handler, true); // capture phase
      addTeardown(function () {
        document.removeEventListener("click", handler, true);
      });
    }

    // ---- delay / exit_intent -----------------------------------------------
    var isTouch =
      (window.matchMedia && window.matchMedia("(hover: none), (pointer: coarse)").matches) || "ontouchstart" in window;

    function armDelay(name, seconds) {
      var ms = (parseInt(seconds, 10) || 0) * 1000;
      var id = setTimeout(function () {
        fireOnce(name);
      }, ms);
      addTeardown(function () {
        clearTimeout(id);
      });
    }
    function armExitIntent() {
      if (isTouch) {
        armDelay("exit_intent", ENTRY.display_delay_seconds || 5);
        return;
      }
      function h(e) {
        if (e.clientY <= 0) fireOnce("exit_intent");
      }
      document.addEventListener("mouseout", h);
      addTeardown(function () {
        document.removeEventListener("mouseout", h);
      });
    }

    // ---- cart_value (shared fetch-wrap + one /cart.js read; no new poller) --
    function cartThresholdCents() {
      var v = ENTRY.min_cart_value;
      if (typeof v !== "number" || !(v > 0)) return null;
      return Math.round(v * 100);
    }
    function evalCartValue(cart, threshold) {
      if (fired || !cart) return;
      if (typeof cart.total_price === "number" && cart.total_price >= threshold) fireOnce("cart_value");
    }
    var cartReadPending = false;
    function readCartAndEval(threshold) {
      if (fired || cartReadPending) return;
      cartReadPending = true;
      origFetch("/cart.js", { headers: { Accept: "application/json" } })
        .then(function (r) {
          return r.json();
        })
        .then(function (cart) {
          cartReadPending = false;
          evalCartValue(cart, threshold);
        })
        .catch(function () {
          cartReadPending = false;
        });
    }
    // Extract added variant id(s) from a /cart/add.js response body. Handles:
    //  - JSON multi-item add:  { items: [ {variant_id/id, ...}, ... ] }
    //  - single-item add:      { id, variant_id, ..., sections?: {...} }
    //    (Dawn's FormData add returns the line item at top level, alongside a
    //     `sections` key — the extra keys are ignored.)
    // Returns [] when the variant can't be determined.
    function addedVariantIds(body) {
      var ids = [];
      function push(o) {
        if (!o) return;
        var v = o.variant_id != null ? o.variant_id : o.id;
        var n = Number(v);
        if (v != null && !isNaN(n)) ids.push(n);
      }
      if (body && Array.isArray(body.items)) body.items.forEach(push);
      else push(body);
      return ids;
    }
    // Extract the added product's HANDLE from a /cart/add.js response (the parent the
    // upsell should be tied to via _upsell_for). Same body shapes as addedVariantIds.
    function addedParentHandle(body) {
      var o = body && Array.isArray(body.items) ? body.items[0] : body;
      return (o && typeof o.handle === "string" && o.handle) || null;
    }

    function entryHasAddTrigger(e) {
      return normalizeTriggers(e && e.display_trigger).indexOf("add_to_cart") !== -1;
    }
    // Per-bundle: pick which eligible add_to_cart entry matches THIS add. An entry
    // scoped by trigger_variants matches when the added variant is in it; otherwise
    // an entry scoped by trigger_products matches when the added product handle is in
    // it; an entry with neither condition matches any add (generic fallback).
    // Specific (variant/product) matches win over generic; ties go to priority
    // (eligibleEntries is already priority-sorted).
    function pickEntryForAdd(handle, variantIds) {
      // Skip entries whose frequency cap is already met — so on a re-armed page a
      // once_session bundle won't fire twice, while `always` bundles fire every add.
      var cands = eligibleEntries.filter(function (e) {
        return entryHasAddTrigger(e) && !isCapped(e);
      });
      if (!cands.length) return null;
      var specific = [];
      var generic = [];
      cands.forEach(function (e) {
        var tv = e.trigger_variants || [];
        var tp = e.trigger_products || [];
        if (tv.length) {
          if (
            variantIds.some(function (id) {
              return tv.indexOf(id) !== -1;
            })
          )
            specific.push(e);
        } else if (tp.length) {
          if (handle && tp.indexOf(handle) !== -1) specific.push(e);
        } else {
          generic.push(e);
        }
      });
      var pool = specific.length ? specific : generic;
      return pool.length ? pool[0] : null;
    }

    var CART_MUT_RE = /\/cart\/(add|change|update)(\.js)?/;
    var addInFlight = null; // latest theme /cart/add the add_to_cart trigger saw (updateFreeShipping waits on it)
    function noop() {}
    function installCartWrap(wantAddToCart, cartThreshold) {
      var prevFetch = window.fetch;
      if (typeof prevFetch !== "function") return;
      // The theme's cart drawer (assets/cart-drawer.js) opens on the add's first
      // fetch layer, while slower layers in between can hold the request ~0.7s
      // before it reaches this wrapper, so the drawer flashed open before the gate
      // below started. It emits td-cart:open synchronously in open(), before its
      // 'active' class lands, so gating here hides it before it ever paints. Only
      // for an add (is-adding; a cart-icon click opens it too) and only while an
      // add_to_cart entry can still fire. The timer reveals the drawer if the add
      // never reaches this wrapper (every path below that does not fire unmasks it).
      if (wantAddToCart) {
        var earlyGateTimer = null;
        document.addEventListener("td-cart:open", function () {
          if (fired || tdPopupAdding || resumeSuppress || drawerAdding()) return;
          var drawer = document.querySelector("cart-drawer");
          if (!drawer || !drawer.classList.contains("is-adding")) return;
          var canFire = eligibleEntries.some(function (e) {
            return entryHasAddTrigger(e) && !isCapped(e);
          });
          if (!canFire) return;
          startCartGate();
          if (earlyGateTimer) clearTimeout(earlyGateTimer);
          earlyGateTimer = setTimeout(function () {
            earlyGateTimer = null;
            if (!fired) endCartGate();
          }, 6000);
        });
      }
      window.fetch = function () {
        var a0 = arguments[0];
        var url = (a0 && (a0.url || a0)) || "";
        var p = prevFetch.apply(this, arguments);
        if (!fired && !tdPopupAdding && !resumeSuppress && !drawerAdding() && typeof url === "string") {
          if (wantAddToCart && /\/cart\/add(\.js)?/.test(url)) {
            // Mask the drawer NOW — the moment the add is in flight — so the theme's
            // / UpCart's auto-open (which fires on the RESPONSE) never flashes before
            // the popup. If we end up NOT firing, endCartGate() unmasks it.
            startCartGate();
            addInFlight = p;
            // Capture the parent product handle (the bundle just added) for the
            // anti-scam _upsell_for link. Clone — never consume the original body.
            p.then(function (res) {
              if (!res || !res.ok) return;
              res
                .clone()
                .json()
                .then(function (b) {
                  var h = addedParentHandle(b);
                  if (h) lastAddedParentHandle = h;
                })
                .catch(function () {});
            }).catch(function () {});
            // Only instant-fire when there is exactly ONE add-eligible entry with no
            // product/variant condition. Otherwise read the response and pick the entry
            // whose trigger_products/variants match the added item (per-bundle upsells).
            var addCands = eligibleEntries.filter(entryHasAddTrigger);
            var soloGeneric =
              addCands.length === 1 &&
              !(addCands[0].trigger_variants || []).length &&
              !(addCands[0].trigger_products || []).length;
            if (soloGeneric) {
              ENTRY = addCands[0];
              fireOnce("add_to_cart");
            } else {
              p.then(function (res) {
                if (fired || tdPopupAdding || resumeSuppress || drawerAdding() || !res || !res.ok) {
                  endCartGate();
                  return;
                }
                res
                  .clone()
                  .json()
                  .then(function (body) {
                    if (fired || tdPopupAdding || resumeSuppress || drawerAdding()) {
                      endCartGate();
                      return;
                    }
                    var ids = addedVariantIds(body);
                    var handle = addedParentHandle(body);
                    if (handle) lastAddedParentHandle = handle;
                    var picked = pickEntryForAdd(handle, ids);
                    if (picked) {
                      ENTRY = picked; // per-bundle: show THIS bundle's upsells
                      fireOnce("add_to_cart");
                    } else {
                      endCartGate(); // no entry matches this add → stay armed
                    }
                  })
                  .catch(function () {
                    endCartGate();
                    failsafeEvent("variant_unknown");
                  });
              }).catch(function () {
                endCartGate();
              });
            }
          }
          if (cartThreshold != null && CART_MUT_RE.test(url)) {
            p.then(function () {
              if (!fired) readCartAndEval(cartThreshold);
            }).catch(function () {});
          }
        }
        return p;
      };
    }

    // ---- Arm ----------------------------------------------------------------
    function normalizeTriggers(dt) {
      var arr = Array.isArray(dt) ? dt : dt ? [dt] : [];
      var seen = {};
      var out = [];
      arr.forEach(function (t) {
        t = String(t || "").trim();
        if (t && !seen[t]) {
          seen[t] = true;
          out.push(t);
        }
      });
      return out;
    }

    function arm() {
      if (sessionDisabled()) return;

      // Layer 1: BUILD BEFORE ARM. If build throws, never arm — button untouched.
      try {
        buildModal();
        popupUsable = true;
      } catch (e) {
        popupUsable = false;
        failsafeEvent("build_failed");
        log("build failed — popup disabled", e);
        return;
      }

      var triggers = normalizeTriggers(ENTRY.display_trigger);
      // add_to_cart is shared across ALL eligible entries (per-bundle selection at
      // add-time); click/delay/exit are armed for the single-winner ENTRY below.
      var anyAdd = eligibleEntries.some(entryHasAddTrigger);
      if (!triggers.length && !anyAdd) {
        log("entry has no display_trigger");
        return;
      }
      var wantAddToCart = anyAdd;
      var threshold = triggers.indexOf("cart_value") !== -1 ? cartThresholdCents() : null;
      if (triggers.indexOf("cart_value") !== -1 && threshold == null) {
        log("cart_value trigger without a valid min_cart_value — skipping");
      }

      if (wantAddToCart || threshold != null) installCartWrap(wantAddToCart, threshold);
      if (threshold != null) readCartAndEval(threshold);

      triggers.forEach(function (t) {
        if (t === "delay") armDelay("delay", ENTRY.display_delay_seconds);
        else if (t === "exit_intent") armExitIntent();
        else if (t === "click") armClick();
        else if (t === "add_to_cart" || t === "cart_value") {
          /* handled by the shared cart wrap */
        } else {
          log("unknown display_trigger", t);
        }
      });
    }

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", arm);
    } else {
      arm();
    }

    // Manual opener for debugging (bypasses triggers, not kill switches).
    window.tdUpsellPopupOpen = function () {
      if (!popupUsable) {
        try {
          buildModal();
          popupUsable = true;
        } catch (e) {
          return;
        }
      }
      FIRED_TRIGGER = FIRED_TRIGGER || "manual";
      fired = true;
      disarmAll();
      showVerified();
    };
  }
})();
